import axios from "axios";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PersonPlus, ShieldCheck, ArrowLeft } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./CreateClientPage.css";
import { useAuthContext } from "../../hooks/useAuthContext";

export default function CreateClientPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  const [clientPortalEnabled, setClientPortalEnabled] = useState(false);
  const [canSelfBookAppointments, setCanSelfBookAppointments] = useState(false);

  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [err, setErr] = useState("");

  const { user } = useAuthContext();

  const cleanEmail = email.trim().toLowerCase();
  const cleanConfirmEmail = confirmEmail.trim().toLowerCase();

  const emailHasStarted = cleanEmail.length > 0;
  const emailsMatch = cleanEmail === cleanConfirmEmail;

  const emailIsValid =
    !cleanEmail || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanEmail);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setConfirmEmail("");
    setClientPortalEnabled(false);
    setCanSelfBookAppointments(false);
    setValidated(false);
    setComplete(false);
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setValidated(true);
    setErr("");

    if (!firstName.trim()) {
      setErr("First name is required.");
      return;
    }

    if (!lastName.trim()) {
      setErr("Last name is required.");
      return;
    }

    if (clientPortalEnabled && !cleanEmail) {
      setErr("This client must have an email address to access the client portal.");
      return;
    }

    if (cleanEmail && !emailIsValid) {
      setErr("Enter a valid email address.");
      return;
    }

    if (emailHasStarted && !emailsMatch) {
      setErr("Email addresses do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const createClient = {
        FirstName: firstName.trim(),
        LastName: lastName.trim(),
        Email: cleanEmail ? cleanEmail : null,
        ClientPortalEnabled: clientPortalEnabled,
        CanSelfBookAppointments: clientPortalEnabled
          ? canSelfBookAppointments
          : false,
      };

      const response = await axios.post(
        `${ApiConnectionString}/api/Client`,
        createClient,
        {
          headers: {
            Authorization: `bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        setComplete(true);
      }
    } catch (error) {
      console.error(error);

      if (typeof error.response?.data === "string") {
        setErr(error.response.data);
      } else {
        setErr("Failed to create client.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="create-client-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (complete) {
    return (
      <div className="create-client-page">
        <div className="create-client-card success-card">
          <div className="create-client-icon success-icon">
            <ShieldCheck />
          </div>

          <h1>Client created</h1>

          <p>
            <strong>
              {firstName} {lastName}
            </strong>{" "}
            has been added successfully.
          </p>

          <div className="create-client-summary">
            <div>
              <strong>Email</strong>
              <span>{cleanEmail ? cleanEmail : "No email saved"}</span>
            </div>

            <div>
              <strong>Client portal</strong>
              <span>{clientPortalEnabled ? "Enabled" : "Disabled"}</span>
            </div>

            {clientPortalEnabled && (
              <div>
                <strong>Self-booking</strong>
                <span>
                  {canSelfBookAppointments ? "Allowed" : "Not allowed"}
                </span>
              </div>
            )}
          </div>

          {clientPortalEnabled && (
            <div className="create-client-warning">
              <ShieldCheck />
              <p>
                An invite email has been sent so the client can verify their
                email and create a password.
              </p>
            </div>
          )}

          <button className="btn create-client-btn w-100" onClick={resetForm}>
            Add another client
          </button>

          <Link to="/clients" className="create-client-link">
            Back to clients
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="create-client-page">
      <div className="create-client-top">
        <Link to="/clients" className="create-client-back">
          <ArrowLeft /> Back to clients
        </Link>
      </div>

      <div className="create-client-card">
        <div className="create-client-header">
          <div className="create-client-icon">
            <PersonPlus />
          </div>

          <div>
            <h1>Create client</h1>
            <p>Add a client to your therapy diary.</p>
          </div>
        </div>

        {err && <div className="alert alert-danger">{err}</div>}

        <form
          className={validated ? "was-validated" : ""}
          onSubmit={submit}
          noValidate
        >
          <section className="create-client-section">
            <h2>Client details</h2>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">First name</label>

                <input
                  className={`form-control create-client-input ${
                    validated && !firstName.trim() ? "is-invalid" : ""
                  }`}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />

                <div className="invalid-feedback">Enter first name</div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Last name</label>

                <input
                  className={`form-control create-client-input ${
                    validated && !lastName.trim() ? "is-invalid" : ""
                  }`}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />

                <div className="invalid-feedback">Enter last name</div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Email address{" "}
                {!clientPortalEnabled && (
                  <span className="text-muted">(optional)</span>
                )}
              </label>

              <input
                type="email"
                className={`form-control create-client-input ${
                  validated &&
                  ((clientPortalEnabled && !cleanEmail) || !emailIsValid)
                    ? "is-invalid"
                    : ""
                }`}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);

                  if (!e.target.value.trim()) {
                    setConfirmEmail("");
                  }
                }}
              />

              <div className="invalid-feedback">
                {clientPortalEnabled && !cleanEmail
                  ? "This client must have an email address to access the client portal"
                  : "Enter a valid email address"}
              </div>
            </div>

            {emailHasStarted && (
              <div className="mb-3">
                <label className="form-label">Confirm email address</label>

                <input
                  type="email"
                  className={`form-control create-client-input ${
                    validated && !emailsMatch ? "is-invalid" : ""
                  }`}
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                />

                <div className="invalid-feedback">
                  Email addresses must match
                </div>
              </div>
            )}

            {emailHasStarted && emailsMatch && confirmEmail && (
              <div className="create-client-note">
                <ShieldCheck />
                <p>Email address confirmed.</p>
              </div>
            )}
          </section>

          <section className="create-client-section">
            <h2>Access</h2>

            <label className="permission-card">
              <input
                type="checkbox"
                checked={clientPortalEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;

                  setClientPortalEnabled(enabled);

                  if (!enabled) {
                    setCanSelfBookAppointments(false);
                  }
                }}
              />

              <div>
                <strong>Enable client portal</strong>
                <span>Client can log in and view appointments.</span>
              </div>
            </label>

            {clientPortalEnabled && (
              <>
                <label className="permission-card">
                  <input
                    type="checkbox"
                    checked={canSelfBookAppointments}
                    onChange={(e) =>
                      setCanSelfBookAppointments(e.target.checked)
                    }
                  />

                  <div>
                    <strong>Allow self-booking</strong>
                    <span>Client can book available appointment times.</span>
                  </div>
                </label>

                <div className="create-client-warning">
                  <ShieldCheck />
                  <p>
                    An invite email will be sent when the client is created.
                  </p>
                </div>
              </>
            )}
          </section>

          <button type="submit" className="btn create-client-btn w-100">
            Create client
          </button>
        </form>
      </div>
    </div>
  );
}